import React from "react";
import PropTypes from "prop-types";
import { compose } from "redux";
import PDPCarousel from "components/source/pdp/pdp-carousel";
import PDPOutfitStyles from "./pdp-outfit-styles";
import sharedActions from "actions/shared-actions";
import carouselTypes from "helpers/carousel-types";
import productsActions from "actions/products-actions";
import MembershipUpgradesHelpers from "helpers/membership-upgrades-helpers";
import { browserPropType, userDataPropType } from "components/propTypes";
import { connect } from "react-redux";
import { withFeatureFlags, featureFlagsPropType, Flags } from "components/source/hoc/with-feature-flags";
import { mapDiscoProductToCarouselProduct } from "helpers/disco-filter-helpers";

class PDPCarousels extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    product: PropTypes.shape({
      id: PropTypes.string,
      pdpCarouselList: PropTypes.arrayOf(PropTypes.string),
      category: {
        id: PropTypes.string,
      },
      occasions: PropTypes.array,
    }),
    isAccessory: PropTypes.bool,
    browser: browserPropType,
    userData: userDataPropType,
    featureFlags: featureFlagsPropType,
    workingFilters: PropTypes.object,
    annotationSuffix: PropTypes.string,
    isRentable: PropTypes.bool,
    loading: PropTypes.bool.isRequired,
    membershipPricing: PropTypes.array,
    // membershipState is used in MembershipUpgradesHelpers.shouldRefreshUpgradePricingCe
    // eslint-disable-next-line react/no-unused-prop-types
    membershipState: PropTypes.object,
    shortlists: PropTypes.arrayOf(PropTypes.object),
    displayedModal: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    favorites: PropTypes.arrayOf(PropTypes.string),
    relatedProducts: PropTypes.array,
    collectionCarouselSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
      url: PropTypes.string,
    }),
    recommendationCarouselSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
      url: PropTypes.string,
    }),
    rentTheLookSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
    }),
    similarStylesSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
      url: PropTypes.string,
    }),
    youMayAlsoLikeSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
      url: PropTypes.string,
    }),
    clearanceCarouselSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
    }),
    fetchRentTheLookCarousel: PropTypes.func,
    fetchSimilarStylesCarousel: PropTypes.func,
    fetchYouMayAlsoLikeCarousel: PropTypes.func,
    fetchCollectionCarousel: PropTypes.func,
    fetchRecommendationCarousel: PropTypes.func,
    fetchClearanceCarousel: PropTypes.func,
    authDataHasLoaded: PropTypes.bool,
    staticProduct: PropTypes.object,
    merchandisingCategory: PropTypes.string,
  };

  //If workingFilters and authData are available on mount, call carousels immediately
  //This would typically be when the user interacts with the PDP Drawer on grids
  componentDidMount() {
    const {
      authDataHasLoaded,
      workingFilters,
      product: { pdpCarouselList = [] },
    } = this.props;
    if (authDataHasLoaded && workingFilters && pdpCarouselList.length) {
      this.fetchCarousels();
    }
  }

  //If we did not have workingFilters and authData on mount, or we need to update pricing, call carousels
  //This would typically be when the user loads a full PDP
  componentDidUpdate(prevProps) {
    const {
      authDataHasLoaded,
      product: { pdpCarouselList = [] },
      workingFilters,
    } = this.props;

    const preRequisiteState = pdpCarouselList.length && authDataHasLoaded && workingFilters;
    const shouldRefreshUpgradePricing = MembershipUpgradesHelpers.shouldRefreshUpgradePricingCe(prevProps, this.props);
    const stateCheck =
      preRequisiteState && (!prevProps.workingFilters || !prevProps.authDataHasLoaded || shouldRefreshUpgradePricing);

    if (stateCheck) {
      this.fetchCarousels(shouldRefreshUpgradePricing);
    }
  }

  isClearanceCategory() {
    return this.props.merchandisingCategory === "Clearance";
  }

  closeModal = () => {
    this.props.dispatch(sharedActions.displayModal(false));
  };

  fetchCarousels(isUpgradePricingRefresh) {
    const {
      fetchCollectionCarousel,
      fetchSimilarStylesCarousel,
      fetchRentTheLookCarousel,
      fetchYouMayAlsoLikeCarousel,
      fetchRecommendationCarousel,
      fetchClearanceCarousel,
      product: { id: productId },
      userData,
      workingFilters,
      youMayAlsoLikeSuccess,
      staticProduct,
    } = this.props;

    const category = staticProduct?.category;
    const additionalAttributes = staticProduct?.additionalAttributes;

    if (this.isClearanceCategory()) {
      fetchClearanceCarousel(category);
    } else {
      if (this.shouldFetchOutfits()) {
        fetchRentTheLookCarousel(productId);
      }
      if (this.shouldFetchSimilarStyles() || isUpgradePricingRefresh) {
        fetchSimilarStylesCarousel(productId, userData, workingFilters);
      }
      if (!youMayAlsoLikeSuccess || isUpgradePricingRefresh) {
        fetchYouMayAlsoLikeCarousel(productId, userData, workingFilters);
      }

      fetchCollectionCarousel("new_arrivals_app", category);

      // Create a recommendations carousel for simialr occasions
      if (category && Array.isArray(additionalAttributes) && additionalAttributes.length > 0) {
        const useCaseEventId = "useCaseEvent";
        const useCaseEvent = additionalAttributes?.find(attr => attr.id === useCaseEventId)?.values;
        fetchRecommendationCarousel({ category, useCaseEvent: useCaseEvent });
      }
    }
  }

  shouldFetchOutfits = () => {
    const { featureFlags, product, rentTheLookSuccess } = this.props;

    return (
      !rentTheLookSuccess &&
      featureFlags?.[Flags.PDP_HOW_WE_STYLED_IT] &&
      product.pdpCarouselList.includes(carouselTypes.OUTFITS)
    );
  };

  shouldFetchSimilarStyles = () => {
    const { featureFlags, product, similarStylesSuccess } = this.props;
    return (
      !similarStylesSuccess &&
      featureFlags?.[Flags.VISUALLY_SIMILAR_TO_FLAG] &&
      product.pdpCarouselList.includes(carouselTypes.VISUALLY_SIMILAR_TO)
    );
  };

  reorderCarouselsByOrder(carouselList, sortingOrder) {
    return [...carouselList].sort((a, b) => sortingOrder.indexOf(a.type) - sortingOrder.indexOf(b.type));
  }

  reorderCarousels(carouselList) {
    const visuallySimilar = carouselList.find(carousel => carousel.type === carouselTypes.VISUALLY_SIMILAR_TO);

    if (!visuallySimilar) return carouselList;

    return this.reorderCarouselsByOrder(carouselList, [
      carouselTypes.VISUALLY_SIMILAR_TO,
      carouselTypes.OUTFITS,
      carouselTypes.SIMILAR_ITEMS,
      carouselTypes.RECENTLY_VIEWED,
      carouselTypes.CLEARANCE,
    ]);
  }

  reorderCarouselsForNoIntent = carouselList => {
    const sortingOrder = [
      carouselTypes.OUTFITS,
      carouselTypes.VISUALLY_SIMILAR_TO,
      carouselTypes.RECOMMENDATION,
      carouselTypes.COLLECTION,
      carouselTypes.SIMILAR_ITEMS,
      carouselTypes.CLEARANCE,
    ];

    const reordered = [...carouselList];
    reordered.sort((a, b) => sortingOrder.indexOf(a.type) - sortingOrder.indexOf(b.type));

    return reordered;
  };

  renderCarousels(carouselList) {
    return (
      <div className="pdp-carousel-wrapper">
        {carouselList.map((relatedProductObject, i) => {
          if (relatedProductObject.type === carouselTypes.OUTFITS) {
            // Outfits carousel differs from the other pdp carousels
            return (
              <PDPOutfitStyles
                key={i}
                products={relatedProductObject.products}
                title={relatedProductObject.title}
                subtitle={relatedProductObject.subtitle}
                pixelName={relatedProductObject.pixelName}
                url={relatedProductObject.url}
                type={relatedProductObject.type}
                annotationSuffix={this.props.annotationSuffix}
                isAccessory={this.props.isAccessory}
                closeModal={this.closeModal}
                dispatch={this.props.dispatch}
                displayedModal={this.props.displayedModal}
                favorites={this.props.favorites}
                browser={this.props.browser}
                filters={this.props.workingFilters}
                isRentable={this.props.isRentable}
                loading={this.props.loading}
                membershipPricing={this.props.membershipPricing}
                product={this.props.product}
                shortlists={this.props.shortlists}
                userData={this.props.userData}
                // Always override img reorder for visually
                noModelImageFirst={true}
              />
            );
          } else {
            let carouselProducts;

            if (
              relatedProductObject.type === carouselTypes.COLLECTION ||
              relatedProductObject.type === carouselTypes.RECOMMENDATION ||
              relatedProductObject.type === carouselTypes.CLEARANCE
            ) {
              carouselProducts = relatedProductObject?.products?.map(relatedProduct =>
                mapDiscoProductToCarouselProduct(relatedProduct, this.isClearanceCategory())
              );
            } else {
              carouselProducts = relatedProductObject.products;
            }

            return (
              <PDPCarousel
                key={i}
                products={carouselProducts}
                title={relatedProductObject.title}
                pixelName={relatedProductObject.pixelName}
                subtitle={relatedProductObject.subtitle}
                url={relatedProductObject.url}
                type={relatedProductObject.type}
                annotationSuffix={this.props.annotationSuffix}
                closeModal={this.closeModal}
                dispatch={this.props.dispatch}
                displayedModal={this.props.displayedModal}
                favorites={this.props.favorites}
                filters={this.props.workingFilters}
                isRentable={this.props.isRentable}
                loading={this.props.loading}
                membershipPricing={this.props.membershipPricing}
                noModelImageFirst={relatedProductObject.type === carouselTypes.VISUALLY_SIMILAR_TO}
                product={this.props.product}
                shortlists={this.props.shortlists}
                userData={this.props.userData}
                isClearance={this.isClearanceCategory()}
              />
            );
          }
        })}
      </div>
    );
  }

  render() {
    const { relatedProducts = [], rentTheLookSuccess, similarStylesSuccess, youMayAlsoLikeSuccess } = this.props;
    // products can sometimes be undefined so need to filter out those values
    const pdpCarousels = [...relatedProducts].filter(Boolean);
    if (youMayAlsoLikeSuccess) {
      pdpCarousels.push(youMayAlsoLikeSuccess);
    }

    if (rentTheLookSuccess) {
      // default ordering of carousels put RTL carousel beginning of carousel order - AS 6/2/23
      pdpCarousels.unshift(rentTheLookSuccess);
    }

    if (similarStylesSuccess) {
      // default ordering of carousels put VS carousel at end of carousel order - AS 6/2/23
      pdpCarousels.push(similarStylesSuccess);
    }

    // Add new arrivals Carousel
    if (this.props.collectionCarouselSuccess) {
      pdpCarousels.push(this.props.collectionCarouselSuccess);
    }

    if (this.props.recommendationCarouselSuccess) {
      pdpCarousels.push(this.props.recommendationCarouselSuccess);
    }

    // Adds the clearance carousel, if there are more than 5 items in clearance.
    if (this.props.clearanceCarouselSuccess && this.props.clearanceCarouselSuccess?.products?.length > 5) {
      pdpCarousels.push(this.props.clearanceCarouselSuccess);
    }

    if (!pdpCarousels.length) {
      return null;
    }

    // Order should be persistent regardless of intent
    return this.renderCarousels(this.reorderCarouselsForNoIntent(pdpCarousels));
  }
}

const mapStateToProps = state => {
  return {
    membershipState: state.membershipState,
    rentTheLookSuccess: state.rentTheLookSuccess,
    collectionCarouselSuccess: state.collectionCarouselSuccess,
    recommendationCarouselSuccess: state.recommendationCarouselSuccess,
    similarStylesSuccess: state.similarStylesSuccess,
    youMayAlsoLikeSuccess: state.youMayAlsoLikeSuccess,
    clearanceCarouselSuccess: state.clearanceCarouselSuccess,
    userData: state.userData,
    ceBagCount: state.ceBagCount,
    authDataHasLoaded: state.userData?.authDataHasLoaded,
    staticProduct: state?.staticProduct,
    merchandisingCategory: state.staticProduct?.merchandisingCategory,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    fetchRentTheLookCarousel: productId => {
      dispatch(productsActions.fetchRentTheLook(productId));
    },
    fetchSimilarStylesCarousel: (productId, userData, workingFilters) => {
      dispatch(productsActions.fetchSimilarStyles(productId, userData, workingFilters));
    },
    fetchYouMayAlsoLikeCarousel: (productId, userData, workingFilters) => {
      dispatch(productsActions.fetchYouMayAlsoLike(productId, userData, workingFilters));
    },
    fetchCollectionCarousel: (collectionId, category) => {
      dispatch(productsActions.fetchCollectionCarousel(collectionId, category));
    },
    fetchRecommendationCarousel: (query, title) => {
      dispatch(productsActions.fetchRecommendationCarousel(query, title));
    },
    fetchClearanceCarousel: category => {
      dispatch(productsActions.fetchClearanceCarousel(category));
    },
  };
};

export default compose(
  withFeatureFlags(Flags.PDP_HOW_WE_STYLED_IT, Flags.VISUALLY_SIMILAR_TO_FLAG),
  connect(mapStateToProps, mapDispatchToProps)
)(PDPCarousels);
